import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

interface Venue {
  id: string;
  name: string;
  address: string;
}

interface Coach {
  id: string;
  displayName: string;
  email: string;
  coachBio: string;
  coachSpecialization: string;
  coachExperience: string;
  coachHourlyRate: string;
  coachVenues: string[];
  coachWeekSchedule: DaySchedule[];
  photoURL?: string;
}

interface DaySchedule {
  day: string;
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface CoachMatch {
  id: string;
  venueName: string;
  venueId: string;
  date: Date;
  startTime: string;
  endTime: string;
  coachId?: string;
  coachName?: string;
  playerIds: string[];
  playerNames: string[];
  status: string;
  isPublic: boolean;
  needsCoach: boolean;
}

const Training: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'coaches' | 'matches'>('coaches');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [matches, setMatches] = useState<CoachMatch[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<CoachMatch | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load venues
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      const venuesData = venuesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Venue))
        .filter(venue => (venue as any).status === 'active');
      setVenues(venuesData);

      // Load coaches
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const coachesData: Coach[] = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.isCoach) {
          let photoURL = userData.photoURL || '';
          
          // Try to get photo from Firebase Storage
          if (!photoURL) {
            try {
              const storage = getStorage();
              const photoRef = ref(storage, `coaches/${userDoc.id}/profile.jpg`);
              photoURL = await getDownloadURL(photoRef);
            } catch (error) {
              // No photo uploaded yet
              photoURL = '';
            }
          }

          coachesData.push({
            id: userDoc.id,
            displayName: userData.displayName || 'Unknown',
            email: userData.email || '',
            coachBio: userData.coachBio || '',
            coachSpecialization: userData.coachSpecialization || '',
            coachExperience: userData.coachExperience || '',
            coachHourlyRate: userData.coachHourlyRate || '',
            coachVenues: userData.coachVenues || [],
            coachWeekSchedule: userData.coachWeekSchedule || [],
            photoURL
          });
        }
      }
      setCoaches(coachesData);

      // Load public matches that need a coach
      const matchesQuery = query(
        collection(db, 'bookings'),
        where('isPublic', '==', true),
        where('needsCoach', '==', true),
        orderBy('date', 'asc')
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      const matchesData = matchesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          venueName: data.venueName || '',
          venueId: data.venueId || '',
          date: data.date.toDate(),
          startTime: data.startTime || '',
          endTime: data.endTime || '',
          coachId: data.coachId,
          coachName: data.coachName,
          playerIds: data.playerIds || [],
          playerNames: data.playerNames || [],
          status: data.status || 'pending',
          isPublic: data.isPublic || false,
          needsCoach: data.needsCoach || false
        } as CoachMatch;
      });
      setMatches(matchesData);
    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCoaches = selectedVenue === 'all'
    ? coaches
    : coaches.filter(coach => coach.coachVenues.includes(selectedVenue));

  const getDayName = (day: string) => {
    const days: { [key: string]: string } = {
      monday: i18n.language === 'hu' ? 'Hétfő' : 'Monday',
      tuesday: i18n.language === 'hu' ? 'Kedd' : 'Tuesday',
      wednesday: i18n.language === 'hu' ? 'Szerda' : 'Wednesday',
      thursday: i18n.language === 'hu' ? 'Csütörtök' : 'Thursday',
      friday: i18n.language === 'hu' ? 'Péntek' : 'Friday',
      saturday: i18n.language === 'hu' ? 'Szombat' : 'Saturday',
      sunday: i18n.language === 'hu' ? 'Vasárnap' : 'Sunday'
    };
    return days[day] || day;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">
          {i18n.language === 'hu' ? 'Edzés' : 'Training'}
        </h1>
        <p className="text-[#B8B8C3]">
          {i18n.language === 'hu' ? 'Edzők és edző meccsek kezelése' : 'Manage coaches and coach matches'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-[#31383D]">
        <button
          onClick={() => setActiveTab('coaches')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'coaches'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          {i18n.language === 'hu' ? 'Edzők' : 'Coaches'}
        </button>
        <button
          onClick={() => setActiveTab('matches')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'matches'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          {i18n.language === 'hu' ? 'Edző meccsek' : 'Coach Matches'}
        </button>
      </div>

      {/* Coaches Tab */}
      {activeTab === 'coaches' && (
        <div className="space-y-6">
          {/* Venue Filter */}
          <div className="card-dark p-4">
            <label className="block text-sm font-semibold text-[#B8B8C3] mb-2">
              {i18n.language === 'hu' ? 'Helyszín szűrő' : 'Venue Filter'}
            </label>
            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="input-dark w-full max-w-md"
            >
              <option value="all">{i18n.language === 'hu' ? 'Összes helyszín' : 'All Venues'}</option>
              {venues.map(venue => (
                <option key={venue.id} value={venue.id}>{venue.name}</option>
              ))}
            </select>
          </div>

          {/* Coaches Grid */}
          {filteredCoaches.length === 0 ? (
            <div className="text-center py-12 text-[#B8B8C3]">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p>{i18n.language === 'hu' ? 'Nincs elérhető edző ezen a helyszínen' : 'No coaches available at this venue'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCoaches.map(coach => (
                <div
                  key={coach.id}
                  className="card-dark p-6 hover:bg-[#15191E] transition-all cursor-pointer"
                  onClick={() => setSelectedCoach(coach)}
                >
                  {/* Coach Photo */}
                  <div className="flex items-center gap-4 mb-4">
                    {coach.photoURL ? (
                      <img
                        src={coach.photoURL}
                        alt={coach.displayName}
                        className="w-20 h-20 rounded-full object-cover border-2 border-cyan-500"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-2xl font-bold">
                        {coach.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-white">{coach.displayName}</h3>
                      <p className="text-sm text-cyan-400">{coach.coachSpecialization || (i18n.language === 'hu' ? 'Általános edző' : 'General Coach')}</p>
                    </div>
                  </div>

                  {/* Coach Info */}
                  <div className="space-y-2 text-sm">
                    {coach.coachExperience && (
                      <div className="flex items-center gap-2 text-[#B8B8C3]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{coach.coachExperience} {i18n.language === 'hu' ? 'év tapasztalat' : 'years experience'}</span>
                      </div>
                    )}
                    {coach.coachHourlyRate && (
                      <div className="flex items-center gap-2 text-green-400 font-semibold">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{coach.coachHourlyRate} Ft/óra</span>
                      </div>
                    )}
                    {coach.coachBio && (
                      <p className="text-[#B8B8C3] line-clamp-2 mt-3">{coach.coachBio}</p>
                    )}
                  </div>

                  <button className="btn-secondary w-full mt-4">
                    {i18n.language === 'hu' ? 'Részletek megtekintése' : 'View Details'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Coach Matches Tab */}
      {activeTab === 'matches' && (
        <div className="space-y-4">
          {matches.length === 0 ? (
            <div className="text-center py-12 text-[#B8B8C3]">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p>{i18n.language === 'hu' ? 'Nincs aktív edző meccs' : 'No active coach matches'}</p>
            </div>
          ) : (
            matches.map(match => (
              <div
                key={match.id}
                className="card-dark p-6 hover:bg-[#15191E] transition-all cursor-pointer"
                onClick={() => setSelectedMatch(match)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{match.venueName}</h3>
                    <p className="text-sm text-[#B8B8C3]">
                      {match.date.toLocaleDateString(i18n.language === 'hu' ? 'hu-HU' : 'en-US')} • {match.startTime} - {match.endTime}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    match.coachId ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {match.coachId 
                      ? (i18n.language === 'hu' ? 'Edző hozzárendelve' : 'Coach Assigned')
                      : (i18n.language === 'hu' ? 'Edző keresése' : 'Looking for Coach')}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-[#B8B8C3]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>{match.playerIds.length} {i18n.language === 'hu' ? 'játékos' : 'players'}</span>
                  </div>
                  {match.coachName && (
                    <div className="flex items-center gap-2 text-cyan-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>{match.coachName}</span>
                    </div>
                  )}
                </div>

                <button className="btn-secondary w-full mt-4">
                  {i18n.language === 'hu' ? 'Részletek megtekintése' : 'View Details'}
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Coach Details Modal */}
      {selectedCoach && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedCoach(null)}>
          <div className="card-dark max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  {selectedCoach.photoURL ? (
                    <img
                      src={selectedCoach.photoURL}
                      alt={selectedCoach.displayName}
                      className="w-24 h-24 rounded-full object-cover border-2 border-cyan-500"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-3xl font-bold">
                      {selectedCoach.displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedCoach.displayName}</h2>
                    <p className="text-cyan-400">{selectedCoach.coachSpecialization || (i18n.language === 'hu' ? 'Általános edző' : 'General Coach')}</p>
                    <p className="text-sm text-[#B8B8C3]">{selectedCoach.email}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedCoach(null)} className="text-[#B8B8C3] hover:text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Info */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">{i18n.language === 'hu' ? 'Információk' : 'Information'}</h3>
                  <div className="space-y-2">
                    {selectedCoach.coachExperience && (
                      <div className="flex items-center gap-2 text-[#B8B8C3]">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{selectedCoach.coachExperience} {i18n.language === 'hu' ? 'év tapasztalat' : 'years experience'}</span>
                      </div>
                    )}
                    {selectedCoach.coachHourlyRate && (
                      <div className="flex items-center gap-2 text-green-400 font-semibold">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{selectedCoach.coachHourlyRate} Ft/óra</span>
                      </div>
                    )}
                  </div>
                  {selectedCoach.coachBio && (
                    <p className="text-[#B8B8C3] mt-4">{selectedCoach.coachBio}</p>
                  )}
                </div>

                {/* Venues */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">{i18n.language === 'hu' ? 'Helyszínek' : 'Venues'}</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCoach.coachVenues.map(venueId => {
                      const venue = venues.find(v => v.id === venueId);
                      return venue ? (
                        <span key={venueId} className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm">
                          {venue.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>

                {/* Schedule */}
                {selectedCoach.coachWeekSchedule && selectedCoach.coachWeekSchedule.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">{i18n.language === 'hu' ? 'Órarend' : 'Schedule'}</h3>
                    <div className="space-y-2">
                      {selectedCoach.coachWeekSchedule
                        .filter(day => day.enabled)
                        .map(day => (
                          <div key={day.day} className="flex justify-between items-center p-3 bg-[#15191E] rounded-lg">
                            <span className="text-white font-semibold">{getDayName(day.day)}</span>
                            <span className="text-cyan-400">{day.startTime} - {day.endTime}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Match Details Modal */}
      {selectedMatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedMatch(null)}>
          <div className="card-dark max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">{selectedMatch.venueName}</h2>
                  <p className="text-[#B8B8C3]">
                    {selectedMatch.date.toLocaleDateString(i18n.language === 'hu' ? 'hu-HU' : 'en-US')} • {selectedMatch.startTime} - {selectedMatch.endTime}
                  </p>
                </div>
                <button onClick={() => setSelectedMatch(null)} className="text-[#B8B8C3] hover:text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Status */}
                <div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    selectedMatch.coachId ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {selectedMatch.coachId 
                      ? (i18n.language === 'hu' ? 'Edző hozzárendelve' : 'Coach Assigned')
                      : (i18n.language === 'hu' ? 'Edző keresése' : 'Looking for Coach')}
                  </span>
                </div>

                {/* Coach Info */}
                {selectedMatch.coachName && (
                  <div>
                    <h3 className="text-lg font-bold text-white mb-3">{i18n.language === 'hu' ? 'Edző' : 'Coach'}</h3>
                    <div className="flex items-center gap-2 text-cyan-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-semibold">{selectedMatch.coachName}</span>
                    </div>
                  </div>
                )}

                {/* Players */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">
                    {i18n.language === 'hu' ? 'Játékosok' : 'Players'} ({selectedMatch.playerIds.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedMatch.playerNames.map((playerName, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-[#15191E] rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold">
                          {playerName.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white">{playerName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Training;
